/**
 * The dog -- the visible half of server/src/dog.js, drawn from the server's
 * word and nothing else.
 *
 * Same authority split as the prey renderer: the simulation lives on the
 * server because the tag is contested state, and this file receives one row
 * at the snapshot cadence, smooths it, and animates what the mode says is
 * happening.  It never decides a position, a chase or a catch -- there is
 * not one gameplay number in here, only sizes, colours and wags.  With no
 * server there are simply no rows and no dog, which keeps the published
 * artifact build a working garden (the group starts invisible and only a
 * row can show it).
 *
 * WHY PRIMITIVES AND NOT THE CAT PIPELINE.  The cats are rigged GLBs out of
 * cats/gen's Python; a dog through that pipeline is a new species of asset
 * for one ambient character.  The level builds an entire street out of
 * boxes and spheres, and the prey renderer already proved a critter reads
 * at fog distance if its SILHOUETTE and MOTION carry it -- so the dog is a
 * dozen primitives whose job is silhouette (deep chest, heavy head, hound
 * ears, up-tail) and whose job two is motion (trot, wag, bounce).  What
 * says "dog" from thirty metres is none of the geometry: it is a shape
 * bigger than any cat moving at a pace no cat uses, tail going.
 *
 * Unlit with a baked belly-dark gradient, exactly the prey's material
 * bargain and for the same reason: another agent owns the sky and the
 * lighting, and an unlit body with its own painted form reads identically
 * under every sun they ship.  (The gradient trick is prey.js's shade(),
 * re-derived here per-mesh because that one is written against instanced
 * geometry and this dog is the one thing in the game that is deliberately
 * NOT an InstancedMesh: there is one of him.)
 *
 * TONE: everything animated here says pet, not threat.  The tail never
 * stops -- it wags FASTER in a chase, because to the dog this is the best
 * game on the street -- the ears are floppy, there is a red collar so he
 * is visibly somebody's, and the "woof" after a tag is a bouncing,
 * planted, delighted bark, not a lunge.
 */
import * as THREE from "three";
import { DogCol, DOG_ROW_LEN } from "@catworld/shared";
import { DOG_MODES } from "@catworld/shared";

/** How hard the render position chases the wire, 1/s -- the prey's 12,
    for the prey's reason: ~80 ms of lag keeps the dog beside the cat it
    is chasing on every screen instead of leading it. */
const CHASE = 12;
/** Past this, the move was a server restart, not motion: snap. */
const SNAP_DIST = 2.5;

// Coat: warm retriever tan, darker hound ears, cream muzzle, black nose,
// and the red collar that says "somebody's pet" at any distance.
const COAT = 0xc08a52;
const EARS = 0x8a5a30;
const MUZZLE = 0xe8d5b4;
const NOSE = 0x35291f;
const COLLAR = 0xd23c2e;

/** Bake a belly-dark vertical gradient into vertex colour so the unlit
    material still models form; the material's own colour multiplies over
    the greyscale.  lo..hi is the geometry's y span in its own frame. */
function shade(geom, lo, hi) {
  const pos = geom.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, (pos.getY(i) - lo) / (hi - lo)));
    const v = 0.6 + 0.4 * t;
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
  }
  geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return geom;
}

function part(geom, colour) {
  return new THREE.Mesh(geom, new THREE.MeshBasicMaterial({
    color: colour, vertexColors: true,
  }));
}

export class Dog {
  constructor() {
    this.group = new THREE.Group();
    this.group.visible = false;       // until the first row says otherwise

    // Everything is built nose toward +z, the scene's one heading
    // convention, at a shoulder of ~0.45 m -- two thirds again a cat's
    // 0.27, which is the size relationship the mechanic reads by.

    // Torso: a deep-chested ellipsoid, 0.75 long.  The chest sits forward
    // and low (the sphere is scaled then nudged) so the profile is a
    // dog's wedge rather than the prey's even eggs.
    const torsoG = new THREE.SphereGeometry(0.30, 12, 9);
    torsoG.scale(0.52, 0.62, 1.25);
    shade(torsoG, -0.19, 0.19);
    this.torso = part(torsoG, COAT);
    this.torso.position.set(0, 0.42, 0);

    // Head, on its own pivot so sniffing and barking are one rotation.
    this.headPivot = new THREE.Group();
    this.headPivot.position.set(0, 0.55, 0.30);

    const headG = new THREE.SphereGeometry(0.13, 10, 8);
    headG.scale(0.85, 0.95, 1.0);
    shade(headG, -0.12, 0.12);
    const head = part(headG, COAT);
    head.position.set(0, 0.05, 0.05);

    const snoutG = new THREE.SphereGeometry(0.06, 8, 6);
    snoutG.scale(0.75, 0.65, 1.5);
    shade(snoutG, -0.04, 0.04);
    const snout = part(snoutG, MUZZLE);
    snout.position.set(0, 0.005, 0.17);

    const noseG = shade(new THREE.SphereGeometry(0.023, 6, 5), -0.02, 0.02);
    const nose = part(noseG, NOSE);
    nose.position.set(0, 0.015, 0.255);

    // Hound ears: flat drooping slabs hung high on the head, pivoted at
    // the top edge so the flop is one rotation about z.
    this.earL = this.ear(-1);
    this.earR = this.ear(1);

    this.headPivot.add(head, snout, nose, this.earL, this.earR);

    // Collar: a red band at the neck line, between torso and head.
    const collarG = new THREE.TorusGeometry(0.105, 0.024, 6, 14);
    shade(collarG, -0.03, 0.03);
    const collar = part(collarG, COLLAR);
    collar.rotation.x = Math.PI / 2 - 0.35;
    collar.position.set(0, 0.475, 0.24);

    // Legs: four cylinders pivoted at the hip, so the trot is a rotation.
    // Order matters to nobody but the animator: [FL, FR, RL, RR].
    this.legs = [
      this.leg(-0.11, 0.24), this.leg(0.11, 0.24),
      this.leg(-0.11, -0.22), this.leg(0.11, -0.22),
    ];

    // Tail: pivoted at the rump, carried high (an anxious dog carries it
    // low, and this dog has never been anxious in its life); the wag is a
    // rotation about the pivot's y.
    this.tailPivot = new THREE.Group();
    this.tailPivot.position.set(0, 0.52, -0.34);
    this.tailPivot.rotation.x = -0.9;         // up and back
    const tailG = new THREE.CylinderGeometry(0.016, 0.032, 0.30, 6);
    tailG.translate(0, 0.15, 0);              // pivot at the root
    shade(tailG, 0, 0.3);
    this.tailPivot.add(part(tailG, COAT));

    this.group.add(this.torso, this.headPivot, collar, this.tailPivot,
                   ...this.legs);

    // Render state: current smoothed pose chasing the latest row.
    this.cur = new THREE.Vector3();
    this.tgt = new THREE.Vector3();
    this.heading = 0;
    this.tgtHeading = 0;
    this.mode = "patrol";
    this.target = -1;
    this.speed = 0;                   // smoothed, derived from cur's motion
    this.prev = new THREE.Vector3();
    this.phase = 0;                   // legs
    this.wagPhase = 0;                // tail
    this.woofPhase = 0;               // the bark bounce
    this.headDown = 0;                // 0..1, eased into a sniff

    // Same reduced-motion posture as the prey: the flourish rates halve,
    // the dog itself stays -- it is gameplay, and must remain findable.
    this.calm = false;
    try {
      this.calm = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    } catch {}
  }

  /** Add this to the scene; like the prey it is not part of the level build
      (builder.js merges, and merged geometry cannot wag). */
  get object3D() {
    return this.group;
  }

  ear(side) {
    const g = new THREE.SphereGeometry(0.055, 7, 6);
    g.scale(0.35, 1.15, 0.75);
    g.translate(0, -0.05, 0);         // pivot at the top edge -> flop rotates
    shade(g, -0.11, 0.01);
    const e = part(g, EARS);
    e.position.set(side * 0.10, 0.115, 0.03);
    e.rotation.z = side * 0.35;       // resting droop, away from the skull
    return e;
  }

  leg(x, z) {
    const g = new THREE.CylinderGeometry(0.032, 0.040, 0.40, 7);
    g.translate(0, -0.20, 0);         // pivot at the hip -> the trot rotates
    shade(g, -0.4, 0);
    const l = part(g, COAT);
    l.position.set(x, 0.40, z);
    return l;
  }

  /** The latest dog row off the wire (DogCol order).  Trusted the way every
      server message is; the guard is shape, not suspicion. */
  setRow(row) {
    if (!Array.isArray(row) || row.length < DOG_ROW_LEN) return;
    this.tgt.set(row[DogCol.X], row[DogCol.Y], row[DogCol.Z]);
    this.tgtHeading = row[DogCol.HEADING];
    this.mode = DOG_MODES[row[DogCol.MODE]] ?? "patrol";
    this.target = row[DogCol.TARGET];
    if (!this.group.visible) {
      // First word from the server: appear where the dog IS, not lerp in
      // from the origin through three gardens.
      this.cur.copy(this.tgt);
      this.prev.copy(this.tgt);
      this.heading = this.tgtHeading;
      this.group.visible = true;
    }
  }

  update(dt) {
    if (!this.group.visible) return;
    dt = Math.min(dt || 0, 0.05);
    if (dt <= 0) return;
    const calmF = this.calm ? 0.5 : 1;

    // ---- chase the wire ----
    if (this.cur.distanceToSquared(this.tgt) > SNAP_DIST * SNAP_DIST) {
      this.cur.copy(this.tgt);
      this.heading = this.tgtHeading;
    } else {
      this.cur.lerp(this.tgt, 1 - Math.exp(-dt * CHASE));
      // The turn chases harder in a chase: a dog wheeling after a dodging
      // cat that turned a beat late reads as a different, slower animal.
      const turn = this.mode === "chase" ? 14 : 9;
      this.heading += shortestArc(this.heading, this.tgtHeading) *
        Math.min(1, dt * turn);
    }

    // Ground speed out of the smoothing itself, smoothed again: it drives
    // the gait, and the raw per-frame value gutters at low frame rates.
    const inst = this.cur.distanceTo(this.prev) / dt;
    this.prev.copy(this.cur);
    this.speed += (inst - this.speed) * Math.min(1, dt * 8);

    // ---- the body ----
    this.group.position.copy(this.cur);
    this.group.rotation.y = this.heading;

    // ---- legs: a trot, diagonal pairs in antiphase ----
    // Stride frequency rides the actual ground speed so the legs can never
    // pedal while the wire has him standing; amplitude saturates at the
    // patrol trot and the chase reads faster through frequency alone.
    this.phase += this.speed * 5.5 * calmF * dt;
    const amp = Math.min(0.62, this.speed * 0.55);
    const swing = Math.sin(this.phase * Math.PI * 2) * amp;
    this.legs[0].rotation.x = swing;      // FL with RR,
    this.legs[3].rotation.x = swing;
    this.legs[1].rotation.x = -swing;     // FR with RL: the trot's diagonals
    this.legs[2].rotation.x = -swing;

    // ---- tail: never still, faster the better the game is going ----
    const wagRate = this.mode === "woof" ? 16 : this.mode === "chase" ? 12
                  : this.mode === "sniff" ? 4 : 7;
    const wagAmp = this.mode === "woof" ? 0.7 : this.mode === "chase" ? 0.55
                 : 0.35;
    // The wag drives rotation.z, not y, and the choice is load-bearing:
    // the geometry runs along the pivot's +y and three.js Eulers apply Ry
    // FIRST (XYZ order), so a y-wag would spin the tail about its own long
    // axis -- invisibly.  Rz swings the +y shaft side to side, and the
    // pivot's fixed x-tilt then carries that swing up into the air where
    // the whole street can read it.
    this.wagPhase += wagRate * calmF * dt;
    this.tailPivot.rotation.z = Math.sin(this.wagPhase) * wagAmp;

    // ---- head: down for a sniff, up and busy for a bark ----
    const wantDown = this.mode === "sniff" ? 1 : 0;
    this.headDown += (wantDown - this.headDown) * Math.min(1, dt * 6);
    let pitch = this.headDown * 0.55;

    let bounce = 0;
    if (this.mode === "woof") {
      // The triumph: quick front-paws bounces with the muzzle tossing up
      // on each one -- barking as a whole-body activity, the way an
      // actually delighted dog does it.  Nothing here moves the group in
      // plan, so the theatre can never disagree with the server's spot.
      this.woofPhase += 9 * calmF * dt;
      const w = Math.abs(Math.sin(this.woofPhase * Math.PI));
      bounce = w * 0.045;
      pitch -= w * 0.35;
    } else {
      this.woofPhase = 0;
    }
    this.headPivot.rotation.x = pitch;
    this.torso.position.y = 0.42 + bounce;
    this.headPivot.position.y = 0.55 + bounce;

    // ---- ears: flop against the motion, a beat behind the legs ----
    const flop = Math.sin(this.phase * Math.PI * 2 - 0.8) *
      Math.min(0.18, this.speed * 0.12 + (this.mode === "woof" ? 0.10 : 0));
    this.earL.rotation.z = -0.35 + flop;
    this.earR.rotation.z = 0.35 + flop;
  }
}

function shortestArc(from, to) {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
